'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import { Box, Text } from '@chakra-ui/react';
import productService from '../../_service/product.service';
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { IRedux } from '../../_store/types';
import { useTranslations } from 'next-intl';
import useEmblaCarousel from 'embla-carousel-react';
import { useActiveBrand } from '../../_hooks/useActiveBrand';
import ProductCard from '../Card';
import useProductDiscount from '../../_hooks/useProductDiscount';

const Favourites = ({ selectedShipperId }: { selectedShipperId?: string }) => {
  const cartState = useSelector((state: IRedux) => state.cart);
  const commonState = useSelector((state: IRedux) => state.common);

  const activeShipperId = useActiveBrand();
  const shipperId = selectedShipperId || activeShipperId;
  const branch = commonState?.branches?.[shipperId];
  const { discounts } = useProductDiscount();

  const t = useTranslations();
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    slidesToScroll: 1,
    dragFree: true,
  });

  const isDesktop = useIsDesktop();

  // Flatten all products from all brands into a single array
  const cart = useMemo(() => {
    if (
      !cartState?.cart ||
      typeof cartState.cart !== 'object' ||
      Array.isArray(cartState.cart)
    ) {
      return [];
    }
    return Object.values(cartState.cart).flatMap(
      (brandCart) => brandCart?.products || [],
    );
  }, [cartState?.cart]);

  const categoryIds = useMemo(
    () => String(cart?.map((product) => product.category_id) || ''),
    [cart],
  );

  const { data } = useQuery({
    queryKey: ['product-favourites', categoryIds, shipperId],
    queryFn: () =>
      productService
        .getProductFavourites(
          {
            category_ids: categoryIds,
            menu_id: branch?.menu_id || '',
          },
          shipperId,
        )
        .then((res) => res.data),
  });

  if (!data || data?.favourites?.length === 0) {
    return null;
  }

  return (
    <Box as='section' pb={{ base: 1, lg: 3 }} pt={{ base: 5, lg: 8 }}>
      <Text
        px={{ base: 4, lg: 14 }}
        lineHeight={1}
        fontSize={{ base: 'xl', lg: '2xl' }}
        fontWeight={700}
      >
        {t('shall_we_add_something_else')}
      </Text>
      <Box
        className='embla'
        style={
          {
            '--slide-size': isDesktop ? '286px' : '204px',
            '--slide-spacing': isDesktop ? '16px' : '8px',
          } as React.CSSProperties
        }
      >
        <Box
          px={{ base: 4, lg: 14 }}
          py={{ base: 4, lg: 5 }}
          className='embla__viewport'
          ref={(el: HTMLDivElement | null) => {
            emblaRef(el);
          }}
        >
          <Box className='embla__container'>
            {data?.favourites?.map((product) => (
              <div
                className='embla__slide'
                key={`${product.id}-${product?.image}`}
              >
                <ProductCard
                  category={null}
                  discounts={discounts}
                  product={product}
                />
              </div>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Favourites;
